// Design System - Gettel
// Sistema de design unificado para toda a aplicação

export const DesignSystem = {
  // ===== CORES PRIMÁRIAS =====
  colors: {
    // Gradiente Principal (Lilás Claro/Azul Leve)
    primary: {
      start: '#E6E6FA',
      end: '#B0C4DE',
      gradient: 'linear-gradient(135deg, #E6E6FA 0%, #B0C4DE 100%)',
    },

    // Azul Leve (para componentes)
    secondary: {
      start: '#B0C4DE',
      end: '#87CEEB',
      gradient: 'linear-gradient(135deg, #B0C4DE, #87CEEB)',
    },

    // Fundo das Seções
    section: {
      start: '#f5f7fa',
      end: '#c3cfe2',
      gradient: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
    },

    // Gradiente Lilás Claro (para modais)
    lightPurple: {
      start: '#E6E6FA',
      end: '#D8BFD8',
      gradient: 'linear-gradient(135deg, #E6E6FA 0%, #D8BFD8 100%)',
    },

    // Tons neutros
    neutral: {
      white: '#ffffff',
      gray: {
        light: '#f8f9fa',
        medium: '#e9ecef',
        dark: '#6c757d',
        darker: '#495057',
      },
      black: '#2c3e50',
    },

    // Estados
    status: {
      success: '#27ae60',
      warning: '#f39c12',
      error: '#e74c3c',
      info: '#3498db',
    },
  },

  // ===== SOMBRAS =====
  shadows: {
    // Sombra do modal principal
    modal: '0 20px 60px rgba(0, 0, 0, 0.04)',

    // Sombra do header do modal
    modalHeader: '0 4px 20px rgba(0, 0, 0, 0.04)',

    // Sombra dos cards/componentes
    card: '0 8px 15px rgba(0, 0, 0, 0.08)',

    // Sombra sutil para elementos
    subtle: '0 1px 5px rgba(0, 0, 0, 0.05)',

    // Sombra azul para elementos ativos
    blueAccent: '0 6px 15px rgba(74, 144, 226, 0.3)',
  },

  // ===== BORDA =====
  borderRadius: {
    small: '8px',
    medium: '15px',
    large: '20px',
    round: '25px',
    circle: '50%',
  },

  // ===== FONTE =====
  typography: {
    fontFamily: '"Segoe UI", "Roboto", "Helvetica", "Arial", sans-serif',
    fontSize: {
      xs: '0.75rem',
      sm: '0.85rem',
      md: '1rem',
      lg: '1.1rem',
      xl: '1.3rem',
      xxl: '1.5rem',
    },
    fontWeight: {
      light: 300,
      regular: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
    },
  },

  // ===== ESPAÇAMENTO =====
  spacing: {
    xs: '0.25rem',
    sm: '0.5rem',
    md: '1rem',
    lg: '1.5rem',
    xl: '2rem',
    xxl: '3rem',
  },

  // ===== COMPONENTES =====
  components: {
    modal: {
      borderRadius: '20px',
      boxShadow: '0 20px 60px rgba(0, 0, 0, 0.04)',
      header: {
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        borderRadius: '20px 20px 0 0',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.04)',
      },
      content: {
        background: '#f8f9fa',
        borderRadius: '0 0 20px 20px',
      },
      actions: {
        background: '#f8f9fa',
        borderRadius: '0 0 20px 20px',
      },
    },

    button: {
      // Botão padrão para criar/novo (baseado no Novo Contrato)
      default: {
        background: 'linear-gradient(135deg, #E6E6FA 0%, #B0C4DE 100%)',
        color: '#2c3e50',
        border: 'none',
        borderRadius: '20px',
        padding: '0.8rem 1.5rem',
        fontSize: '1rem',
        fontWeight: 600,
        boxShadow: '0 4px 12px rgba(176, 196, 222, 0.3)',
        transition: 'all 0.3s ease',
        hover: {
          transform: 'translateY(-1px)',
          boxShadow: '0 6px 20px rgba(176, 196, 222, 0.4)',
        },
        leave: {
          transform: 'translateY(0)',
          boxShadow: '0 4px 12px rgba(176, 196, 222, 0.3)',
        },
      },
      primary: {
        background: 'linear-gradient(135deg, #E6E6FA 0%, #B0C4DE 100%)',
        color: '#2c3e50',
        border: 'none',
        borderRadius: '20px',
        padding: '0.6rem 1.5rem',
        fontSize: '1rem',
        fontWeight: 600,
        boxShadow: '0 4px 12px rgba(176, 196, 222, 0.3)',
        transition: 'all 0.3s ease',
      },
      secondary: {
        background: 'white',
        border: '2px solid #6c757d',
        borderRadius: '20px',
        padding: '0.5rem 1.2rem',
        fontSize: '0.85rem',
        fontWeight: 600,
        boxShadow: '0 4px 15px rgba(0, 0, 0, 0.1)',
      },
    },

    tabs: {
      active: {
        background: 'linear-gradient(135deg, #4A90E2, #357ABD)',
        color: 'white',
        border: '1px solid #4A90E2',
        borderRadius: '20px',
        padding: '0.4rem 0.8rem',
        fontSize: '0.85rem',
        fontWeight: 600,
        boxShadow: '0 6px 15px rgba(74, 144, 226, 0.3)',
      },
      inactive: {
        background: 'white',
        color: '#666',
        border: '1px solid #e9ecef',
        borderRadius: '20px',
        padding: '0.4rem 0.8rem',
        fontSize: '0.85rem',
        fontWeight: 600,
        boxShadow: '0 1px 5px rgba(0, 0, 0, 0.05)',
      },
    },

    card: {
      background: 'white',
      borderRadius: '10px',
      boxShadow: '0 8px 15px rgba(0, 0, 0, 0.08)',
      border: '1px solid rgba(255, 255, 255, 0.8)',
    },
  },

  // ===== ANIMAÇÕES =====
  transitions: {
    fast: '0.2s ease',
    normal: '0.3s ease',
    slow: '0.5s ease',
  },
};

// Funções utilitárias
export const getPrimaryGradient = () => DesignSystem.colors.primary.gradient;
export const getSecondaryGradient = () => DesignSystem.colors.secondary.gradient;
export const getSectionGradient = () => DesignSystem.colors.section.gradient;
export const getLightPurpleGradient = () => DesignSystem.colors.lightPurple.gradient;
export const getModalShadow = () => DesignSystem.shadows.modal;
export const getCardShadow = () => DesignSystem.shadows.card;

// Funções para estilos de botão
export const getDefaultButtonStyle = () => DesignSystem.components.button.default;
export const getPrimaryButtonStyle = () => DesignSystem.components.button.primary;
export const getSecondaryButtonStyle = () => DesignSystem.components.button.secondary;

// Função para aplicar efeitos hover/leave nos botões
export const getButtonHoverEffects = (buttonType: 'default' | 'primary' | 'secondary' = 'default') => {
  const buttonStyle = DesignSystem.components.button[buttonType];
  return {
    onMouseEnter: (e: any) => {
      if ('hover' in buttonStyle && buttonStyle.hover) {
        e.currentTarget.style.transform = buttonStyle.hover.transform;
        e.currentTarget.style.boxShadow = buttonStyle.hover.boxShadow;
      }
    },
    onMouseLeave: (e: any) => {
      if ('leave' in buttonStyle && buttonStyle.leave) {
        e.currentTarget.style.transform = buttonStyle.leave.transform;
        e.currentTarget.style.boxShadow = buttonStyle.leave.boxShadow;
      }
    },
  };
};
